import React from 'react';
import { Link } from 'react-router-dom';
import { useLanguage } from '../context/LanguageContext';
import { Linkedin } from 'lucide-react';
import { PRIVACY_POLICY_URL, TERMS_OF_USE_URL } from '../lib/links';

const Footer = () => {
  const { t } = useLanguage();

  const scrollToSection = (id) => {
    const element = document.getElementById(id);
    if (element) {
      element.scrollIntoView({ behavior: 'smooth' });
    }
  };

  const scrollToNetwork = (mode) => {
    scrollToSection('warehouses');
    window.dispatchEvent(new CustomEvent('setNetworkView', { detail: mode }));
  };

  return (
    <footer data-testid="footer" className="bg-black text-white">
      {/* Main footer */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16">
        <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-12">
          {/* Brand column */}
          <div className="lg:col-span-2">
            <img
              src="/logo.png"
              alt="MailAmericas"
              className="h-10 w-auto mb-6"
            />
            <p className="text-gray-400 max-w-md leading-relaxed">
              {t('footer.tagline')}
            </p>

            {/* Social links - Solo LinkedIn */}
            <div className="flex gap-4 mt-6">
              <a
                href="https://ar.linkedin.com/company/mailamericas-mailatinamerica"
                target="_blank"
                rel="noopener noreferrer"
                className="w-10 h-10 border border-white/20 flex items-center justify-center hover:bg-[#7AC142] hover:border-[#7AC142] transition-all"
                data-testid="social-linkedin"
                aria-label={t('a11y.linkedin')}
              >
                <Linkedin className="w-4 h-4" />
              </a>
            </div>
          </div>

          {/* Quick links */}
          <div>
            <h4 className="text-sm font-bold uppercase tracking-wider mb-6">
              {t('footer.quickLinks')}
            </h4>
            <ul className="space-y-3">
              {[
                { key: 'process', onClick: () => scrollToSection('process') },
                { key: 'fulfillment', onClick: () => scrollToSection('fulfillment') },
                { key: 'warehouses', onClick: () => scrollToNetwork('origins') },
                { key: 'destinations', onClick: () => scrollToNetwork('destinations') },
                { key: 'dangerousGoods', onClick: () => scrollToSection('dangerous-goods') },
                { key: 'returns', onClick: () => scrollToSection('returns') },
                { key: 'apiDocs', onClick: () => scrollToSection('api-docs') }
              ].map(({ key, onClick }) => (
                <li key={key}>
                  <button
                    onClick={onClick}
                    className="footer-link text-gray-400 hover:text-[#7AC142] transition-colors"
                    data-testid={`footer-link-${key}`}
                  >
                    {t(`nav.${key}`)}
                  </button>
                </li>
              ))}
              <li>
                <Link
                  to="/account-setup"
                  className="footer-link text-gray-400 hover:text-[#7AC142] transition-colors"
                  data-testid="footer-link-accountSetup"
                >
                  {t('nav.accountSetup')}
                </Link>
              </li>
            </ul>
          </div>

          {/* Legal */}
          <div>
            <h4 className="text-sm font-bold uppercase tracking-wider mb-6">
              {t('footer.legal')}
            </h4>
            <ul className="space-y-3">
              <li>
                <a
                  href={PRIVACY_POLICY_URL}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-gray-400 hover:text-[#7AC142] transition-colors"
                  data-testid="footer-privacy"
                >
                  {t('footer.privacy')}
                </a>
              </li>
              <li>
                <a
                  href={TERMS_OF_USE_URL}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-gray-400 hover:text-[#7AC142] transition-colors"
                  data-testid="footer-terms"
                >
                  {t('footer.terms')}
                </a>
              </li>
            </ul>
          </div>
        </div>
      </div>

      {/* Bottom bar */}
      <div className="border-t border-white/10">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
          <div className="flex flex-col md:flex-row items-center justify-between gap-4">
            <p className="text-gray-500 text-sm">
              © {new Date().getFullYear()} MailAmericas. {t('footer.rights')}
            </p>
          </div>
        </div>
      </div>
    </footer>
  );
};

export default Footer;
